import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  validateContactFields,
  validateName,
  validateRussianPhone,
  validateEmail
} from '../utils/checkoutValidation';
import { getExpertRefCookie } from '../utils/expertRef';

const FREE_DELIVERY_THRESHOLD = 10000;

const CheckoutContainer = styled.div`
  min-height: 100vh;
  background: #f8f9fa;
  padding-top: 100px;
  padding-bottom: 60px;
`;

const CheckoutContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 0 20px;
`;

const CheckoutHeader = styled.div`
  text-align: center;
  margin-bottom: 40px;
`;

const CheckoutTitle = styled.h1`
  font-size: 36px;
  color: #333;
  margin-bottom: 10px;
`;

const CheckoutSubtitle = styled.p`
  color: #666;
  font-size: 18px;
`;

const CheckoutForm = styled.div`
  background: white;
  border-radius: 12px;
  padding: 30px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin-bottom: 30px;
`;

const FormSection = styled.div`
  margin-bottom: 30px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h3`
  font-size: 20px;
  color: #333;
  margin-bottom: 20px;
  border-bottom: 2px solid #ff6b35;
  padding-bottom: 10px;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px;
  border: 1px solid ${props => (props.$invalid ? '#dc3545' : '#ddd')};
  border-radius: 8px;
  font-size: 16px;
  
  &:focus {
    outline: none;
    border-color: ${props => (props.$invalid ? '#dc3545' : '#ff6b35')};
  }
`;

const FieldError = styled.span`
  display: block;
  margin-top: 6px;
  font-size: 13px;
  color: #dc3545;
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 16px;
  min-height: 100px;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: #ff6b35;
  }
`;

const OrderSummary = styled.div`
  background: #f8f9fa;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 30px;
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  
  &:last-child {
    margin-bottom: 0;
    border-top: 1px solid #ddd;
    padding-top: 10px;
    font-weight: 600;
    font-size: 18px;
  }
`;

const SummaryLabel = styled.span`
  color: #666;
`;

const SummaryValue = styled.span`
  color: #333;
`;

const SubmitButton = styled.button`
  width: 100%;
  background: linear-gradient(135deg, #4CAF50 0%, #45a049 100%);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 15px;
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3);
  }
  
  &:disabled {
    background: #ccc;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const BackButton = styled.button`
  width: 100%;
  background: transparent;
  color: #ff6b35;
  border: 2px solid #ff6b35;
  border-radius: 8px;
  padding: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-bottom: 20px;
  
  &:hover {
    background: #ff6b35;
    color: white;
  }
`;

// Стили для выбора доставки
const DeliveryOptions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const DeliveryOption = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  border: 2px solid ${props => props.selected ? '#ff6b35' : props.disabled ? '#ccc' : '#ddd'};
  border-radius: 12px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;
  background: ${props => props.disabled ? '#f5f5f5' : props.selected ? '#fff8f5' : 'white'};
  opacity: ${props => props.disabled ? 0.6 : 1};
  
  &:hover {
    border-color: ${props => props.disabled ? '#ccc' : '#ff6b35'};
  }
  
  input[type="radio"] {
    margin-top: 3px;
    accent-color: #ff6b35;
    width: 18px;
    height: 18px;
    cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  }
  
  input[type="radio"]:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const DeliveryInfo = styled.div`
  flex: 1;
`;

const DeliveryTitle = styled.div`
  font-weight: 600;
  color: #333;
  margin-bottom: 4px;
`;

const DeliveryDescription = styled.div`
  font-size: 14px;
  color: #666;
`;

const DeliveryPrice = styled.div`
  font-weight: 600;
  color: ${props => props.free ? '#28a745' : '#333'};
  white-space: nowrap;
`;

const FreeDeliveryNote = styled.div`
  background: #e8f5e9;
  color: #2e7d32;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 14px;
  margin-top: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const UnavailableItemsList = styled.div`
  margin-top: 8px;
  padding: 8px;
  background: #fff3cd;
  border-left: 3px solid #ffc107;
  border-radius: 4px;
  font-size: 13px;
`;

const UnavailableItem = styled.div`
  margin-bottom: 4px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

// Стили для модального окна выбора ПВЗ 5Post
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 16px;
  width: 90%;
  max-width: 900px;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #eee;
`;

const ModalTitle = styled.h3`
  font-size: 20px;
  color: #333;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #666;
  padding: 5px;
  
  &:hover {
    color: #333;
  }
`;

const ModalBody = styled.div`
  padding: 20px;
  overflow-y: auto;
  flex: 1;
`;

const PvzList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const PvzItem = styled.div`
  padding: 16px;
  border: 2px solid ${props => props.selected ? '#ff6b35' : '#ddd'};
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${props => props.selected ? '#fff8f5' : 'white'};
  
  &:hover {
    border-color: #ff6b35;
  }
`;

const PvzName = styled.div`
  font-weight: 600;
  color: #333;
  margin-bottom: 4px;
`;

const PvzAddress = styled.div`
  font-size: 14px;
  color: #666;
  margin-bottom: 4px;
`;

const PvzWorkingHours = styled.div`
  font-size: 13px;
  color: #888;
`;

const PvzType = styled.span`
  display: inline-block;
  padding: 2px 8px;
  background: ${props => props.type === 'postamat' ? '#e3f2fd' : '#e8f5e9'};
  color: ${props => props.type === 'postamat' ? '#1976d2' : '#388e3c'};
  border-radius: 4px;
  font-size: 12px;
  margin-left: 8px;
`;

const SelectedPvzInfo = styled.div`
  margin-top: 12px;
  padding: 12px;
  background: #f0f7ff;
  border-radius: 8px;
  border-left: 3px solid #2196f3;
`;

const SelectPvzButton = styled.button`
  margin-top: 12px;
  padding: 10px 16px;
  background: #ff6b35;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #e55a2b;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 16px;
  margin-bottom: 16px;
  
  &:focus {
    outline: none;
    border-color: #ff6b35;
  }
`;

const MapToggleRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 12px;
`;

const MapToggleButton = styled.button`
  background: ${props => props.active ? '#ff6b35' : '#f5f5f5'};
  color: ${props => props.active ? '#fff' : '#333'};
  border: 1px solid ${props => props.active ? '#ff6b35' : '#ddd'};
  border-radius: 6px;
  padding: 8px 12px;
  font-size: 14px;
  cursor: pointer;
`;

const MapContainer = styled.div`
  width: 100%;
  height: 420px;
  border: 1px solid #eee;
  border-radius: 10px;
  overflow: hidden;
  margin-bottom: 16px;
`;

const LoadingSpinner = styled.div`
  text-align: center;
  padding: 40px;
  color: #666;
`;

const CheckoutPage = () => {
  const { isAuthenticated, user, token } = useAuth();
  const navigate = useNavigate();
  
  const [checkoutData, setCheckoutData] = useState(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    address: '',
    city: '',
    postalCode: '',
    comment: ''
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const hasPrefilledRef = useRef(false);
  
  // Состояния для доставки
  const [deliveryMethod, setDeliveryMethod] = useState(null); // Будет установлен после загрузки пунктов
  const [deliveryCost, setDeliveryCost] = useState(0);
  const [pickupPoints, setPickupPoints] = useState([]);
  const [pickupAvailability, setPickupAvailability] = useState({});
  
  // Состояния для 5Post ПВЗ
  const [showPvzModal, setShowPvzModal] = useState(false);
  const [pvzList, setPvzList] = useState([]);
  const [pvzLoading, setPvzLoading] = useState(false);
  const [pvzSearch, setPvzSearch] = useState('');
  const [selectedPvz, setSelectedPvz] = useState(null);
  const [pvzError, setPvzError] = useState(null);
  const [fivepostWidgetKey, setFivepostWidgetKey] = useState('');
  const [useWidgetMap, setUseWidgetMap] = useState(false);
  const [widgetError, setWidgetError] = useState(null);
  const widgetInstanceRef = useRef(null);
  
  // Состояния для СДЭК
  const [showCdekModal, setShowCdekModal] = useState(false);
  const [selectedCdekPvz, setSelectedCdekPvz] = useState(null);
  const [cdekDeliveryType, setCdekDeliveryType] = useState(null); // 'office' или 'door'
  const [cdekTariff, setCdekTariff] = useState(null);
  const [, setCdekWidgetKey] = useState('');
  const [cdekYandexApiKey, setCdekYandexApiKey] = useState('');
  const [cdekFromCity, setCdekFromCity] = useState('Отрадный');
  const [, setCdekWidgetInstance] = useState(null);
  const cdekWidgetInstanceRef = useRef(null);

  // Функция загрузки списка ПВЗ 5Post
  const loadFivePostPvz = async (city) => {
    if (!city) {
      city = formData.city || 'Москва';
    }
    
    setPvzLoading(true);
    setPvzError(null);
    try {
      const url = `/api/delivery/5post/pvz?city=${encodeURIComponent(city)}`;
      const response = await fetch(url);
      
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setPvzList(result.data || []);
          setPvzError(null);
        } else {
          setPvzList([]);
          setPvzError(result.error || 'Ошибка получения списка ПВЗ');
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Ошибка загрузки списка ПВЗ 5Post:', errorData);
        setPvzList([]);
        setPvzError(errorData.message || errorData.error || 'Ошибка подключения к серверу');
      }
    } catch (error) {
      console.error('Ошибка загрузки списка ПВЗ 5Post:', error);
      setPvzList([]);
      setPvzError('Ошибка соединения с сервером');
    } finally {
      setPvzLoading(false);
    }
  };

  const loadFivePostWidgetScript = () => {
    if (window?.fivepost?.PickupPointsMap) {
      return Promise.resolve();
    }
    const existing = document.querySelector('script[data-fivepost-widget="true"]');
    if (existing) {
      return new Promise((resolve, reject) => {
        existing.addEventListener('load', resolve, { once: true });
        existing.addEventListener('error', reject, { once: true });
      });
    }
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = '/fivepost-widget.js';
      script.async = true;
      script.dataset.fivepostWidget = 'true';
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Не удалось загрузить виджет 5Post'));
      document.body.appendChild(script);
    });
  };

  // Открытие модального окна выбора ПВЗ
  const openPvzModal = () => {
    setShowPvzModal(true);
    loadFivePostPvz(formData.city);
  };

  // Загрузка скрипта Яндекс.Карт с API ключом
  const loadYandexMapsScript = (apiKey) => {
    return new Promise((resolve, reject) => {
      // Удаляем неудачный скрипт vue-yandex-maps (если был от предыдущей попытки)
      const failedVueYmaps = document.getElementById('vue-yandex-maps');
      if (failedVueYmaps && !window.ymaps) {
        failedVueYmaps.remove();
      }
      
      // Проверяем, не загружен ли уже скрипт
      if (window.ymaps && window.ymaps.ready) {
        window.ymaps.ready(resolve);
        return;
      }
      
      const existingYmaps = document.querySelector('script[src*="api-maps.yandex.ru"]');
      if (existingYmaps) {
        // vue-yandex-maps внутри CDEK виджета ищет script#vue-yandex-maps — используем уже загруженный
        if (!existingYmaps.id) existingYmaps.id = 'vue-yandex-maps';
        if (window.ymaps && window.ymaps.ready) {
          window.ymaps.ready(resolve);
        } else {
          existingYmaps.addEventListener('load', () => {
            if (window.ymaps && window.ymaps.ready) {
              window.ymaps.ready(resolve);
            } else {
              resolve();
            }
          }, { once: true });
        }
        return;
      }
      
      // Загружаем скрипт Яндекс.Карт с API ключом
      // id="vue-yandex-maps" — vue-yandex-maps внутри CDEK виджета проверяет этот элемент и не создаёт свой
      // ВАЖНО: добавляем csp=true для корректной работы с CSP
      const script = document.createElement('script');
      script.id = 'vue-yandex-maps';
      script.src = `https://api-maps.yandex.ru/2.1/?lang=ru_RU&apikey=${encodeURIComponent(apiKey)}&csp=true`;
      script.async = true;
      script.onload = () => {
        if (window.ymaps && window.ymaps.ready) {
          window.ymaps.ready(() => resolve());
        } else {
          resolve();
        }
      };
      script.onerror = () => reject(new Error('Не удалось загрузить Яндекс.Карты'));
      document.head.appendChild(script);
    });
  };

  // Динамическая загрузка виджета СДЭК ПОСЛЕ Яндекс.Карт (чтобы ymaps был с нашим ключом)
  const CDEK_WIDGET_URL = 'https://cdn.jsdelivr.net/npm/@cdek-it/widget@3/dist/cdek-widget.umd.js';

  const loadCdekWidgetScript = () => {
    if (window?.CDEKWidget) {
      return Promise.resolve();
    }
    const existing = document.querySelector('script[src*="cdek-it/widget"]');
    if (existing) {
      return new Promise((resolve, reject) => {
        let attempts = 0;
        const poll = () => {
          if (window?.CDEKWidget) return resolve();
          if (++attempts > 100) return reject(new Error('Виджет СДЭК не инициализировался'));
          setTimeout(poll, 50);
        };
        poll();
      });
    }
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.charset = 'utf-8';
      script.src = CDEK_WIDGET_URL;
      script.async = false; // синхронное выполнение для корректной инициализации CDEKWidget
      script.onload = () => {
        let attempts = 0;
        const poll = () => {
          if (window?.CDEKWidget) return resolve();
          if (++attempts > 100) return reject(new Error('Виджет СДЭК не инициализировался'));
          setTimeout(poll, 50);
        };
        poll();
      };
      script.onerror = () => reject(new Error('Не удалось загрузить виджет СДЭК'));
      document.head.appendChild(script);
    });
  };

  // Инициализация виджета СДЭК
  const initCdekWidget = async () => {if (!showCdekModal) {return;
    }

    const yandexKey = (cdekYandexApiKey || '').trim();
    const isPlaceholder = !yandexKey || yandexKey === 'yandex-api-key' || yandexKey.length < 20;
    if (isPlaceholder) {
      console.error('❌ Ключ Яндекс.Карт не настроен или указан плейсхолдер. Укажите реальный ключ в Админ-панели → Настройки → СДЭК.');
      return;
    }

    try {
      // Сначала загружаем Яндекс.Карты — виджет СДЭК требует их для инициализации
      await loadYandexMapsScript(yandexKey);
      // Затем загружаем скрипт виджета
      await loadCdekWidgetScript();

      if (!window?.CDEKWidget) {
        throw new Error('Виджет СДЭК не доступен после загрузки скрипта');
      }

      // Уничтожаем предыдущий экземпляр, если есть
      if (cdekWidgetInstanceRef.current) {
        try {
          // Виджет СДЭК может не иметь метода destroy, просто очищаем контейнер
          const container = document.getElementById('cdek-map');
          if (container) {
            container.innerHTML = '';
          }
        } catch (e) {
          console.warn('Ошибка при уничтожении предыдущего виджета СДЭК:', e);
        }
        cdekWidgetInstanceRef.current = null;
      }

      // Создаем контейнер для карты, если его нет
      const mapContainer = document.getElementById('cdek-map');

      if (!mapContainer) {
        console.error('Контейнер карты СДЭК не найден');
        return;
      }

      // Рассчитываем вес заказа в граммах
      const totalWeightGrams = checkoutData?.items?.reduce((sum, item) => {
        const itemWeight = item.weight || 100; // граммы
        return sum + (itemWeight * (item.quantity || 1));
      }, 0) || 1000;
      
      // Конвертируем в килограммы (виджет ожидает вес в кг согласно инструкции)
      const totalWeightKg = totalWeightGrams / 1000;

      // Инициализируем виджет СДЭК
      // yandexMapsApiKey - обязательный параметр для CDEK Widget 3.x (2023-2024)// Координаты города Отрадный (центр города) для минимизации запросов к геокодеру
      // Можно использовать координаты вместо строки города, чтобы избежать запросов к геокодеру при инициализации
      const cityCoordinates = {
        'Отрадный': [53.3714, 51.3450],
        'Москва': [55.7558, 37.6173],
        'Москва г': [55.7558, 37.6173],
        'Санкт-Петербург': [59.9343, 30.3351],
        'Самара': [53.2001, 50.15],
        'Самарская область': [53.2001, 50.15],
        'Самара г': [53.2001, 50.15],
        'Тольятти': [53.5303, 49.3461],
        'Сызрань': [53.1662, 48.4746]
      };

      const defaultCity = (formData.city || cdekFromCity || '').trim();
      const cityKey = Object.keys(cityCoordinates).find(k => 
        defaultCity.toLowerCase().startsWith(k.toLowerCase()) || 
        k.toLowerCase().startsWith(defaultCity.toLowerCase()) ||
        defaultCity.toLowerCase().includes(k.toLowerCase())
      );
      const defaultLocation = cityKey ? cityCoordinates[cityKey] : (cityCoordinates[defaultCity] || [53.3714, 51.3450]);

      const widgetConfig = {
        apiKey: yandexKey, // Виджет СДЭК 3.x ожидает apiKey (не yandexMapsApiKey)
        yandexMapsApiKey: yandexKey, // Дублируем на случай, если в новых версиях переименовали
        geocodePath: '/api/delivery/cdek/service?action=geocode',
        defaultLocation: defaultLocation, // Координаты или строка города
        from: cdekFromCity,
        lang: 'rus', // Язык согласно инструкции
        currency: 'RUB', // Валюта согласно инструкции
        goods: [{
          length: 10, // в см согласно инструкции
          width: 10,  // в см согласно инструкции
          height: 10, // в см согласно инструкции
          weight: totalWeightKg // в кг согласно инструкции
        }],
        root: 'cdek-map',
        hideDeliveryOptions: {
          office: false,
          door: false
        },
        tariffs: {
          office: [136, 138],
          door: [137, 139]
        },
        canChoose: true,
        servicePath: '/api/delivery/cdek/service',
        services: [
          { code: "INSURANCE", parameter: "2" }
        ],
        total_sum: checkoutData?.totalPrice || 0,
        onReady: () => {console.log('✅ Виджет СДЭК готов');
        },
        onChoose: (type, tariff, address) => {
          console.log('✅ Выбрана доставка СДЭК:', type, tariff, address);
          
          // Сохраняем выбранные данные
          const cdekData = {
            type: type, // 'office' или 'door'
            tariff: tariff,
            address: address
          };
          
          setCdekDeliveryType(type);
          setCdekTariff(tariff);
          setSelectedCdekPvz({
            ...cdekData,
            formatted: type === 'door' ? address.formatted : `${address.name}, ${address.city}`,
            address: type === 'door' ? address.formatted : (address.address || address.full_address),
            city: type === 'door' ? 
              (address.components?.find(c => c.kind === 'locality')?.name || address.city) : 
              address.city,
            code: type === 'office' ? address.code || address.id : null,
            name: type === 'office' ? address.name : null
          });

          // Автозаполнение полей адреса и города
          if (type === 'door') {
            const city = address.components?.find(c => c.kind === 'locality')?.name || address.city;
            setFormData(prev => ({
              ...prev,
              address: address.formatted || '',
              city: city || ''
            }));
          } else {
            setFormData(prev => ({
              ...prev,
              address: address.address || address.full_address || '',
              city: address.city || ''
            }));
          }

          // Закрываем модальное окно
          setShowCdekModal(false);
        },
        onCalculate: (tariff, address) => {
          console.log('✅ Рассчитан тариф СДЭК:', tariff, address);
        },
        onError: (error) => {
          // #region agent log
          const errorDetails = {
            errorMessage: error?.message || String(error),
            errorType: error?.type,
            errorCode: error?.code,
            errorStack: error?.stack?.substring(0, 300),
            errorString: String(error),
            errorDetails: JSON.stringify(error).substring(0, 500),
            hasYandexMapsApiKey: !!cdekYandexApiKey,
            yandexMapsApiKeyLength: cdekYandexApiKey?.length,
            yandexMapsApiKeyFirstChars: cdekYandexApiKey?.substring(0, 10) || '',
            defaultLocation: widgetConfig.defaultLocation,
            from: widgetConfig.from,
            hasYmaps: !!window.ymaps,
            ymapsReady: window.ymaps?.ready ? 'exists' : 'missing'
          };// #endregion
          
          console.error('❌ Ошибка виджета СДЭК:', error);
        }
      };const widgetInstance = new window.CDEKWidget(widgetConfig);
      
      // #region agent log
      const referrerInfo = {
        documentReferrer: document.referrer,
        windowLocation: window.location.href,
        windowOrigin: window.location.origin,
        windowHostname: window.location.hostname,
        windowProtocol: window.location.protocol,
        expectedReferrerHttps: `https://${window.location.hostname}`,
        expectedReferrerHttp: `http://${window.location.hostname}`,
        expectedReferrerWithPath: window.location.origin,
        isNgrokDomain: window.location.hostname.includes('ngrok'),
        referrerPolicy: document.referrerPolicy || 'default',
        recommendation: `В настройках API ключа в консоли Яндекс.Карт укажите один из вариантов HTTP Referrer:\n1. ${window.location.hostname}\n2. https://${window.location.hostname}\n3. ${window.location.hostname.includes('ngrok') ? '*.ngrok-free.dev (если используете ngrok)' : window.location.hostname}`
      };// #endregion
      
      cdekWidgetInstanceRef.current = widgetInstance;
      setCdekWidgetInstance(widgetInstance);
      
    } catch (error) {console.error('Ошибка инициализации виджета СДЭК:', error);
    }
  };

  // Обработка глобальных ошибок виджета СДЭК
  useEffect(() => {
    const handleWindowError = (event) => {
      // Логируем все ошибки, связанные с Yandex Maps или CDEK
      if (event.message && (event.message.includes('[CDEK]') || event.message.includes('CDEK') || event.message.includes('cdek') || event.message.includes('yandex') || event.message.includes('Yandex') || event.message.includes('geocode'))) {}
    };
    
    const handleUnhandledRejection = (event) => {
      // Логируем все ошибки, связанные с Yandex Maps или CDEK
      if (event.reason && (String(event.reason).includes('[CDEK]') || String(event.reason).includes('CDEK') || String(event.reason).includes('cdek') || String(event.reason).includes('yandex') || String(event.reason).includes('Yandex') || String(event.reason).includes('geocode') || String(event.reason).includes('403'))) {}
    };
    
    window.addEventListener('error', handleWindowError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    
    return () => {
      window.removeEventListener('error', handleWindowError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- global error handlers, cdekYandexApiKey for logging only
  }, []);

  // Инициализация виджета СДЭК при открытии модального окна
  useEffect(() => {
    if (!showCdekModal || !cdekYandexApiKey) {
      // Очищаем контейнер при закрытии
      if (!showCdekModal && cdekWidgetInstanceRef.current) {
        const container = document.getElementById('cdek-map');
        if (container) {
          container.innerHTML = '';
        }
        cdekWidgetInstanceRef.current = null;
      }
      return;
    }

    // Небольшая задержка для рендеринга DOM
    const timer = setTimeout(() => {
      initCdekWidget();
    }, 100);

    return () => {
      clearTimeout(timer);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- initCdekWidget captures widget state
  }, [showCdekModal, cdekYandexApiKey, cdekFromCity, checkoutData]);


  // Выбор ПВЗ
  const handlePvzSelect = (pvz) => {
    setSelectedPvz(pvz);
    setShowPvzModal(false);
    
    // Если выбран ПВЗ, автоматически выбираем 5Post как метод доставки
    setDeliveryMethod('5post');
    
    // Автозаполнение полей адреса и города из выбранной точки выдачи
    const address = pvz.address || pvz.full_address || '';
    const city = pvz.city || '';
    
    setFormData(prev => ({
      ...prev,
      address: address,
      city: city
    }));
    
    console.log('✅ Выбран ПВЗ 5Post:', pvz.name, pvz.address);
  };

  // Инициализация карты 5Post с данными из нашего API (виджет 5Post возвращает 404 на production)
  const initFivePostCustomMap = async () => {
    if (!useWidgetMap || !showPvzModal) return;

    const mapContainer = document.getElementById('fivepost-map-container');
    if (!mapContainer) return;

    if (!cdekYandexApiKey || !cdekYandexApiKey.trim()) {
      setWidgetError('Настройте ключ Яндекс.Карт в админ-панели: Настройки → СДЭК');
      return;
    }

    const pointsWithCoords = (pvzList || []).map(p => {
      const lat = p.latitude ?? p.lat ?? p.raw_data?.address?.lat ?? p.raw_data?.latitude;
      const lng = p.longitude ?? p.lng ?? p.lon ?? p.raw_data?.address?.lng ?? p.raw_data?.longitude;
      const latNum = parseFloat(lat);
      const lngNum = parseFloat(lng);
      return { ...p, _lat: latNum, _lng: lngNum };
    }).filter(p => !Number.isNaN(p._lat) && !Number.isNaN(p._lng));
    if (pointsWithCoords.length === 0) {
      setWidgetError(null);
      return; // Показываем загрузку или пустую карту
    }

    try {
      if (widgetInstanceRef.current?.destroy) {
        try {
          widgetInstanceRef.current.destroy();
        } catch (e) {
          console.warn('Ошибка при уничтожении предыдущей карты:', e);
        }
        widgetInstanceRef.current = null;
      }

      await loadYandexMapsScript(cdekYandexApiKey);

      const mapDiv = document.getElementById('fivepost-map');
      if (!mapDiv) return;

      const firstPoint = pointsWithCoords[0];
      const centerLat = firstPoint._lat ?? 55.75;
      const centerLng = firstPoint._lng ?? 37.62;

      window.ymaps.ready(() => {
        const map = new window.ymaps.Map('fivepost-map', {
          center: [centerLat, centerLng],
          zoom: 11,
          controls: ['zoomControl']
        });

        window.__fivepostPvzList = pointsWithCoords;

        const placemarks = pointsWithCoords.map((pvz, idx) => {
          const lat = pvz._lat ?? 0;
          const lng = pvz._lng ?? 0;
          if (Number.isNaN(lat) || Number.isNaN(lng)) return null;

          const balloonContent = `
            <div style="padding: 8px; max-width: 280px;">
              <div style="font-weight: 600; margin-bottom: 6px;">${(pvz.name || 'Пункт выдачи').replace(/</g, '&lt;')}</div>
              <div style="font-size: 13px; color: #555; margin-bottom: 6px;">${(pvz.address || pvz.full_address || '').replace(/</g, '&lt;')}</div>
              ${(pvz.working_hours || pvz.workingHours) ? `<div style="font-size: 12px; color: #666;">🕐 ${(pvz.working_hours || pvz.workingHours).replace(/</g, '&lt;')}</div>` : ''}
              <button onclick="var l=window.__fivepostPvzList;var p=l&&l[${idx}];if(p&&window.__fivepostSelectPvz)window.__fivepostSelectPvz(p)" 
                style="margin-top: 8px; padding: 8px 16px; background: #64b441; color: white; border: none; border-radius: 6px; cursor: pointer; font-size: 14px;">
                Выбрать
              </button>
            </div>
          `;

          const placemark = new window.ymaps.Placemark([lat, lng], {
            balloonContentHeader: (pvz.name || 'Пункт выдачи').replace(/</g, '&lt;'),
            balloonContentBody: balloonContent,
            hintContent: (pvz.name || pvz.address || '').replace(/</g, '&lt;')
          }, {
            preset: 'islands#greenDotIconWithCaption'
          });

          return placemark;
        }).filter(Boolean);

        placemarks.forEach(pm => map.geoObjects.add(pm));

        if (placemarks.length > 1) {
          map.setBounds(map.geoObjects.getBounds(), { checkZoomRange: true, zoomMargin: 50 });
        }

        window.__fivepostSelectPvz = handlePvzSelect;

        widgetInstanceRef.current = {
          destroy: () => {
            if (map) map.destroy();
            window.__fivepostSelectPvz = null;
            window.__fivepostPvzList = null;
          }
        };
      });

      setWidgetError(null);
    } catch (error) {
      console.error('Ошибка инициализации карты 5Post:', error);
      setWidgetError(error.message || 'Не удалось загрузить карту');
    }
  };

  // Поиск по списку ПВЗ
  const handlePvzSearch = (e) => {
    const value = e.target.value;
    setPvzSearch(value);
  };

  useEffect(() => {
    if (!showPvzModal) {
      return;
    }
    const city = formData.city?.trim();
    if (!city) {
      return;
    }
    const timer = setTimeout(() => {
      loadFivePostPvz(city);
    }, 400);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- loadFivePostPvz stable
  }, [showPvzModal, formData.city]);

  useEffect(() => {
    if (!showPvzModal) {
      return;
    }
    const city = formData.city?.trim();
    if (city) {
      return;
    }
    if (pvzSearch.length < 3) {
      return;
    }
    const timer = setTimeout(() => {
      loadFivePostPvz(pvzSearch);
    }, 400);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- loadFivePostPvz stable
  }, [showPvzModal, formData.city, pvzSearch]);

  // Фильтрация ПВЗ по поисковому запросу
  const filteredPvzList = pvzList.filter(pvz => {
    if (!pvzSearch) return true;
    const search = pvzSearch.toLowerCase();
    return (
      pvz.name?.toLowerCase().includes(search) ||
      pvz.address?.toLowerCase().includes(search) ||
      pvz.city?.toLowerCase().includes(search)
    );
  });

  useEffect(() => {
    let isMounted = true;
    const loadWidgetKey = async () => {
      try {
        const response = await fetch('/api/settings/fivepost');

        if (!response.ok) {
          throw new Error(`Ошибка загрузки ключа виджета 5Post: ${response.status}`);
        }
        const result = await response.json();

        if (!isMounted) {
          return;
        }
        const widgetKey = result.settings?.widgetApiKey || '';
        setFivepostWidgetKey(widgetKey);
      } catch (error) {
        if (!isMounted) {
          return;
        }
        setWidgetError('Не удалось загрузить ключ виджета 5Post');
      }
    };
    loadWidgetKey();
    return () => {
      isMounted = false;
    };
  }, []);

  // Загрузка настроек СДЭК для виджета
  useEffect(() => {
    let isMounted = true;
    const loadCdekSettings = async () => {
      try {
        const response = await fetch('/api/settings/cdek');

        if (!response.ok) {
          throw new Error(`Ошибка загрузки настроек СДЭК: ${response.status}`);
        }

        const result = await response.json();

        if (!isMounted) {
          return;
        }

        if (result.settings) {
          const apiKeyValue = result.settings.apiKey || '';
          const fromCityValue = result.settings.fromCity || 'Отрадный';
          setCdekWidgetKey(apiKeyValue);
          setCdekYandexApiKey(apiKeyValue || '');
          setCdekFromCity(fromCityValue);
        }
      } catch (error) {
        console.error('Ошибка загрузки настроек СДЭК:', error);
      }
    };
    loadCdekSettings();
    return () => {
      isMounted = false;
    };
  }, []);

  // Инициализация виджета при открытии модального окна и изменении настроек
  useEffect(() => {
    if (!showPvzModal || !useWidgetMap) {
      // Уничтожаем виджет при закрытии модального окна или переключении на список
      if (!showPvzModal && widgetInstanceRef.current) {
        try {
          widgetInstanceRef.current.destroy?.();
          widgetInstanceRef.current = null;
        } catch (e) {
          console.warn('Ошибка при уничтожении карты:', e);
        }
      }
      return;
    }

    const timer = setTimeout(() => {
      initFivePostCustomMap();
    }, 100);

    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPvzModal, useWidgetMap, pvzList, cdekYandexApiKey, formData.city]);

  // Уничтожаем карту при размонтировании компонента
  useEffect(() => {
    return () => {
      if (widgetInstanceRef.current) {
        try {
          widgetInstanceRef.current.destroy?.();
        } catch (e) {
          console.warn('Ошибка при уничтожении карты:', e);
        }
      }
    };
  }, []);

  // Функция форматирования часов работы
  const formatWorkingHours = (workingHours) => {
    if (!workingHours) return '';
    if (typeof workingHours === 'string') return workingHours;
    const daysMap = {1: 'Пн', 2: 'Вт', 3: 'Ср', 4: 'Чт', 5: 'Пт', 6: 'Сб', 7: 'Вс'};
    const days = workingHours.days?.map(d => daysMap[d]).join(', ') || '';
    return `${days}: ${workingHours.timeFrom || ''}-${workingHours.timeTo || ''}`;
  };

  // Функция форматирования информации о недоступных товарах
  const formatUnavailableItems = (items) => {
    if (!items || items.length === 0) return null;
    
    const missing = items.filter(item => item.status === 'missing');
    const insufficient = items.filter(item => item.status === 'insufficient');
    
    const messages = [];
    
    // Товары, которых нет на складе
    if (missing.length > 0) {
      missing.forEach(item => {
        messages.push(`Товар "${item.productName}" отсутствует на складе`);
      });
    }
    
    // Товары, которых не хватает
    if (insufficient.length > 0) {
      insufficient.forEach(item => {
        messages.push(`Товар "${item.productName}": требуется ${item.required} шт., доступно ${item.available} шт.`);
      });
    }
    
    return messages;
  };

  useEffect(() => {
    // Проверяем авторизацию
    if (!isAuthenticated()) {
      navigate('/login');
      return;
    }

    // Загружаем данные корзины
    const savedCheckoutData = localStorage.getItem('checkoutData');
    if (!savedCheckoutData) {
      navigate('/cart');
      return;
    }

    try {
      const data = JSON.parse(savedCheckoutData);
      setCheckoutData(data);
    } catch (error) {
      console.error('Ошибка загрузки данных корзины:', error);
      navigate('/cart');
    }
    
    // Загружаем пункты самовывоза
    const loadPickupPoints = async () => {
      try {
        const response = await fetch('/api/delivery/pickup-points');
        if (response.ok) {
          const result = await response.json();
          const points = result.data || [];
          setPickupPoints(points);
          
          // Устанавливаем первый пункт самовывоза по умолчанию, если метод доставки еще не выбран
          if (!deliveryMethod && points.length > 0) {
            setDeliveryMethod(points[0].pickup_code);
          }
        }
      } catch (error) {
        console.error('Ошибка загрузки пунктов самовывоза:', error);
      }
    };
    loadPickupPoints();
  }, [isAuthenticated, navigate, deliveryMethod]);

  // Однократное автозаполнение контактов из профиля при наличии user и checkoutData
  useEffect(() => {
    if (!checkoutData || !user || hasPrefilledRef.current) return;
    const empty =
      !formData.firstName &&
      !formData.lastName &&
      !formData.phone &&
      !formData.email;
    if (!empty) return;
    const updates = {};
    if (user.firstName && typeof user.firstName === 'string') updates.firstName = user.firstName.trim();
    if (user.lastName && typeof user.lastName === 'string') updates.lastName = user.lastName.trim();
    if (user.phone && typeof user.phone === 'string') updates.phone = user.phone.trim();
    if (user.email && typeof user.email === 'string') updates.email = user.email.trim();
    if (Object.keys(updates).length === 0) return;
    setFormData(prev => ({ ...prev, ...updates }));
    hasPrefilledRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- prefill only when checkoutData/user ready; empty check uses formData at run time
  }, [checkoutData, user]);

  // Проверка доступности пунктов самовывоза на основе остатков
  useEffect(() => {
    if (!checkoutData || !checkoutData.items || checkoutData.items.length === 0) {
      return;
    }

    const checkPickupAvailability = async () => {
      try {
        // Подготавливаем данные товаров для запроса
        const items = checkoutData.items.map(item => ({
          id: item.id,
          quantity: item.quantity || 1
        }));

        const response = await fetch('/api/delivery/check-pickup-availability', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ items })
        });

        if (response.ok) {
          const result = await response.json();
          setPickupAvailability(result.data || {});
        } else {
          // В случае ошибки показываем все пункты как доступные (fallback)
          console.error('Ошибка проверки доступности самовывоза');
          setPickupAvailability({});
        }
      } catch (error) {
        console.error('Ошибка проверки доступности самовывоза:', error);
        // В случае ошибки показываем все пункты как доступные (fallback)
        setPickupAvailability({});
      }
    };

    checkPickupAvailability();
  }, [checkoutData]);

  // Автоматическое переключение способа доставки, если выбранный пункт стал недоступным
  useEffect(() => {
    if (!deliveryMethod || !deliveryMethod.startsWith('pickup_')) {
      return; // Не самовывоз, переключение не требуется
    }

    const availability = pickupAvailability[deliveryMethod];
    if (availability && !availability.available) {
      // Выбранный пункт недоступен, переключаем на первый доступный способ
      // Сначала пробуем найти другой доступный пункт самовывоза
      const availablePickup = pickupPoints.find(point => {
        const pointAvailability = pickupAvailability[point.pickup_code];
        return pointAvailability && pointAvailability.available;
      });
      
      if (availablePickup) {
        setDeliveryMethod(availablePickup.pickup_code);
      } else {
        // Если нет доступных пунктов самовывоза, переключаем на СДЭК
        setDeliveryMethod('cdek');
      }
    }
  }, [pickupAvailability, deliveryMethod, pickupPoints]);

  // Рассчитываем стоимость доставки
  useEffect(() => {
    if (!checkoutData) return;
    
    const discountPct = Number(checkoutData.orderDiscountPercent) || 5;
    const discAmt = Number(checkoutData.discountAmount) ||
      Math.floor((checkoutData.physicalSubtotal ?? checkoutData.totalPrice ?? 0) * (discountPct / 100));
    const afterDisc = Math.max(0, (checkoutData.totalPrice || 0) - discAmt);
    const isFree = afterDisc >= FREE_DELIVERY_THRESHOLD;
    const isPickup = deliveryMethod && deliveryMethod.startsWith('pickup_');
    
    if (isPickup || isFree) {
      setDeliveryCost(0);
      return;
    }
    if (deliveryMethod === 'cdek') {
      // Берём реальную стоимость из виджета СДЭК
      if (cdekTariff && cdekTariff.delivery_sum != null) {
        setDeliveryCost(Math.round(cdekTariff.delivery_sum));
      } else {
        setDeliveryCost(0); // Пока не выбран тариф
      }
      return;
    }
    if (deliveryMethod === '5post') {
      // Берём реальную стоимость из виджета 5Post
      if (selectedPvz && selectedPvz.deliveryCost != null) {
        setDeliveryCost(Math.round(selectedPvz.deliveryCost));
      } else {
        // Пробуем рассчитать через API
        const city = formData.city?.trim();
        if (!city) {
          setDeliveryCost(0);
          return;
        }
        const fetchCost = async () => {
          try {
            const body = {
              city,
              order_amount: afterDisc,
              pvz_code: selectedPvz?.code || selectedPvz?.pvz_code
            };
            const headers = {
              'Content-Type': 'application/json',
              ...(token && { Authorization: `Bearer ${token}` })
            };
            const res = await fetch('/api/delivery/calculate/5post', {
              method: 'POST',
              headers,
              body: JSON.stringify(body)
            });
            if (res.ok) {
              const data = await res.json();
              if (data.success && data.data?.cost !== undefined) {
                setDeliveryCost(data.data.cost);
                return;
              }
            }
          } catch (e) {
            console.warn('5Post cost calculation failed:', e);
          }
          setDeliveryCost(0);
        };
        fetchCost();
      }
    }
  }, [deliveryMethod, checkoutData, formData.city, selectedPvz, token, cdekTariff]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (['firstName', 'lastName', 'phone', 'email'].includes(name)) {
      setFieldErrors(prev => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const handleContactBlur = (e) => {
    const { name, value } = e.target;
    let err = null;
    if (name === 'firstName' || name === 'lastName') err = validateName(value);
    else if (name === 'phone') err = validateRussianPhone(value);
    else if (name === 'email') err = validateEmail(value);
    if (!['firstName', 'lastName', 'phone', 'email'].includes(name)) return;
    setFieldErrors(prev => {
      const next = { ...prev };
      if (err) next[name] = err;
      else delete next[name];
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const contactErrors = validateContactFields(formData);
    if (Object.keys(contactErrors).length > 0) {
      setFieldErrors(contactErrors);
      return;
    }
    setFieldErrors({});

    // Проверяем, выбран ли ПВЗ для 5Post
    if (deliveryMethod === '5post' && !selectedPvz) {
      alert('Пожалуйста, выберите пункт выдачи 5Post');
      openPvzModal();
      return;
    }

    // Проверяем, выбран ли способ доставки СДЭК
    if (deliveryMethod === 'cdek' && !selectedCdekPvz) {
      alert('Пожалуйста, выберите способ доставки СДЭК');
      setShowCdekModal(true);
      return;
    }

    setLoading(true);

    try {
      // Подстраховка: перед оплатой обновляем цены товаров из БД,
      // чтобы избежать ошибки "Несоответствие цены заказа"
      let freshItems = checkoutData.items;
      let freshTotalPrice = checkoutData.totalPrice;
      try {
        const priceResults = await Promise.all(
          checkoutData.items.map(async (item) => {
            if (item.itemType === 'digital') {
              const digitalId = item.digitalId || parseInt(String(item.id).replace('digital-', ''), 10);
              return { ...item, id: digitalId, itemType: 'digital' };
            }
            const resp = await fetch(`/api/products/${item.id}`);
            if (resp.ok) {
              const d = await resp.json();
              if (d.success && d.data) {
                const fp = parseFloat(d.data.price);
                if (!isNaN(fp)) return { ...item, price: fp, itemType: 'physical' };
              }
            }
            return { ...item, itemType: item.itemType || 'physical' };
          })
        );
        freshItems = priceResults.map(i => ({
          id: i.itemType === 'digital' ? (i.digitalId || parseInt(String(i.id).replace('digital-', ''), 10)) : i.id,
          quantity: i.quantity,
          itemType: i.itemType || 'physical',
          price: i.price
        }));
        freshTotalPrice = freshItems.reduce((s, i) => s + (i.price * i.quantity), 0);
        if (freshTotalPrice !== checkoutData.totalPrice) {
          console.log(`💰 Цены обновлены перед оплатой: ${checkoutData.totalPrice} → ${freshTotalPrice}`);
        }
      } catch (e) {
        console.warn('Не удалось обновить цены, используем текущие:', e);
      }

      const discountPercent = Number(checkoutData.orderDiscountPercent) || 5;
      const physicalFresh = freshItems
        .filter((i) => (i.itemType || 'physical') !== 'digital')
        .reduce((s, i) => s + i.price * i.quantity, 0);
      const finalDiscount = Math.floor(physicalFresh * (discountPercent / 100));
      const afterDiscount = Math.max(0, freshTotalPrice - finalDiscount);
      const isPickupMethod = deliveryMethod && deliveryMethod.startsWith('pickup_');
      const qualifiesForFreeDelivery = afterDiscount >= FREE_DELIVERY_THRESHOLD;
      const finalDeliveryCost = isPickupMethod || qualifiesForFreeDelivery ? 0 : deliveryCost;
      const finalPriceWithDelivery = (afterDiscount - (checkoutData.pointsToSpend || 0)) + finalDeliveryCost;
      
      const orderData = {
        ...formData,
        items: freshItems,
        totalPrice: freshTotalPrice,
        pointsToSpend: checkoutData.pointsToSpend || 0,
        discountAmount: finalDiscount,
        orderDiscountPercent: discountPercent,
        expertRef: getExpertRefCookie(),
        finalPrice: finalPriceWithDelivery,
        deliveryMethod,
        // Данные 5Post
        fivepostPvzCode: deliveryMethod === '5post' && selectedPvz ? (selectedPvz.code || selectedPvz.pvz_code) : null,
        fivepostPvzAddress: deliveryMethod === '5post' && selectedPvz ? (selectedPvz.address || selectedPvz.full_address) : null,
        // Данные СДЭК
        cdekPvzCode: deliveryMethod === 'cdek' && selectedCdekPvz && cdekDeliveryType === 'office' ? selectedCdekPvz.code : null,
        cdekPvzAddress: deliveryMethod === 'cdek' && selectedCdekPvz ? selectedCdekPvz.address : null,
        cdekDeliveryType: deliveryMethod === 'cdek' ? cdekDeliveryType : null,
        cdekTariffCode: deliveryMethod === 'cdek' && cdekTariff ? cdekTariff.tariff_code || cdekTariff.tarif : null,
        cdekTariffData: deliveryMethod === 'cdek' && cdekTariff ? cdekTariff : null,
        cdekAddressData: deliveryMethod === 'cdek' && selectedCdekPvz ? selectedCdekPvz : null,
        deliveryCost: finalDeliveryCost,
        orderNumber: `ORD-${Date.now()}`, // Генерируем номер заказа
        createdAt: new Date().toISOString(),
        fivepostPvzName: selectedPvz?.name || null,
        fivepostPvzCity: selectedPvz?.city || null,
        fivepostPvzType: selectedPvz?.type || selectedPvz?.pvz_type || null,
        fivepostPvzWorkingHours: selectedPvz?.working_hours || selectedPvz?.workingHours || null
      };
      
      // Сохраняем данные заказа в localStorage
      localStorage.setItem('orderData', JSON.stringify(orderData));
      
      // Перенаправляем на страницу оплаты
      navigate('/payment');
      
    } catch (error) {
      console.error('Ошибка оформления заказа:', error);
      alert('Произошла ошибка при оформлении заказа. Попробуйте еще раз.');
    } finally {
      setLoading(false);
    }
  };
  
  // Получаем название метода доставки для отображения
  const getDeliveryMethodName = (method) => {
    if (method && method.startsWith('pickup_')) {
      const point = pickupPoints.find(p => p.pickup_code === method);
      return point ? `Самовывоз: ${point.name}` : 'Самовывоз';
    }
    if (method === 'cdek') return 'Доставка СДЭК';
    if (method === '5post') return 'Доставка 5Post';
    return method || 'Не выбран';
  };
  
  // Проверяем, нужен ли адрес для выбранного способа доставки
  const needsAddress = !deliveryMethod || !deliveryMethod.startsWith('pickup_');
  
  // Бесплатная доставка при сумме после скидки >= FREE_DELIVERY_THRESHOLD
  const checkoutDiscountPercent = Number(checkoutData?.orderDiscountPercent) || 5;
  const checkoutDiscountAmount = Number(checkoutData?.discountAmount) ||
    Math.floor((checkoutData?.physicalSubtotal ?? checkoutData?.totalPrice ?? 0) * (checkoutDiscountPercent / 100));
  const checkoutAfterDiscount = Math.max(
    0,
    (checkoutData?.totalPrice || 0) - checkoutDiscountAmount
  );
  const isFreeDelivery = checkoutAfterDiscount >= FREE_DELIVERY_THRESHOLD;

  const handleBackToCart = () => {
    navigate('/cart');
  };

  if (!checkoutData) {
    return (
      <CheckoutContainer>
        <CheckoutContent>
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <h2>Загрузка данных...</h2>
          </div>
        </CheckoutContent>
      </CheckoutContainer>
    );
  }

  return (
    <CheckoutContainer>
      <CheckoutContent>
        <CheckoutHeader>
          <CheckoutTitle>Оформление заказа</CheckoutTitle>
          <CheckoutSubtitle>Заполните данные для доставки</CheckoutSubtitle>
        </CheckoutHeader>

        <OrderSummary>
          <SectionTitle>Сумма заказа</SectionTitle>
          <SummaryRow>
            <SummaryLabel>Товары ({checkoutData.items.length})</SummaryLabel>
            <SummaryValue>{checkoutData.totalPrice.toLocaleString()} ₽</SummaryValue>
          </SummaryRow>
          {checkoutDiscountAmount > 0 && (
            <SummaryRow>
              <SummaryLabel>Скидка {checkoutDiscountPercent}%</SummaryLabel>
              <SummaryValue style={{ color: '#2e7d32' }}>-{checkoutDiscountAmount.toLocaleString()} ₽</SummaryValue>
            </SummaryRow>
          )}
          {checkoutData.pointsToSpend > 0 && (
            <SummaryRow>
              <SummaryLabel>Списано баллов</SummaryLabel>
              <SummaryValue style={{ color: '#ff6b35' }}>-{checkoutData.pointsToSpend}</SummaryValue>
            </SummaryRow>
          )}
          <SummaryRow>
            <SummaryLabel>Доставка ({getDeliveryMethodName(deliveryMethod)})</SummaryLabel>
            <SummaryValue style={{ color: deliveryCost === 0 ? '#28a745' : '#333' }}>
              {deliveryCost === 0 ? 'Бесплатно' : `${deliveryCost.toLocaleString()} ₽`}
            </SummaryValue>
          </SummaryRow>
          <SummaryRow>
            <SummaryLabel>К доплате</SummaryLabel>
            <SummaryValue style={{ color: '#2e7d32', fontWeight: '600' }}>
              {(checkoutAfterDiscount - (checkoutData.pointsToSpend || 0) + deliveryCost).toLocaleString()} ₽
            </SummaryValue>
          </SummaryRow>
        </OrderSummary>

        <CheckoutForm>
          <form onSubmit={handleSubmit}>
            <FormSection>
              <SectionTitle>Контактная информация</SectionTitle>

              <FormGroup>
                <Label htmlFor="firstName">Имя *</Label>
                <Input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  onBlur={handleContactBlur}
                  $invalid={!!fieldErrors.firstName}
                  required
                />
                {fieldErrors.firstName && <FieldError>{fieldErrors.firstName}</FieldError>}
              </FormGroup>

              <FormGroup>
                <Label htmlFor="lastName">Фамилия *</Label>
                <Input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  onBlur={handleContactBlur}
                  $invalid={!!fieldErrors.lastName}
                  required
                />
                {fieldErrors.lastName && <FieldError>{fieldErrors.lastName}</FieldError>}
              </FormGroup>

              <FormGroup>
                <Label htmlFor="phone">Телефон *</Label>
                <Input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  onBlur={handleContactBlur}
                  $invalid={!!fieldErrors.phone}
                  required
                />
                {fieldErrors.phone && <FieldError>{fieldErrors.phone}</FieldError>}
              </FormGroup>

              <FormGroup>
                <Label htmlFor="email">Email *</Label>
                <Input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  onBlur={handleContactBlur}
                  $invalid={!!fieldErrors.email}
                  required
                />
                {fieldErrors.email && <FieldError>{fieldErrors.email}</FieldError>}
              </FormGroup>
            </FormSection>

            <FormSection>
              <SectionTitle>Способ получения</SectionTitle>
              <FreeDeliveryNote style={{ marginBottom: '20px', marginTop: '0' }}>
                💡 При заказе от 10 000 ₽ (после скидки) доставка бесплатная
              </FreeDeliveryNote>
              
              <DeliveryOptions>
                {/* Динамическая генерация пунктов самовывоза */}
                {pickupPoints.map((point) => {
                  const availability = pickupAvailability[point.pickup_code];
                  const isAvailable = availability === undefined || availability.available;
                  const isDisabled = !isAvailable;
                  const unavailableItems = availability?.items || [];
                  const itemsMessages = formatUnavailableItems(unavailableItems);
                  
                  return (
                    <DeliveryOption 
                      key={point.pickup_code}
                      selected={deliveryMethod === point.pickup_code}
                      disabled={isDisabled}
                      onClick={() => !isDisabled && setDeliveryMethod(point.pickup_code)}
                    >
                      <input 
                        type="radio" 
                        name="delivery" 
                        value={point.pickup_code}
                        checked={deliveryMethod === point.pickup_code}
                        onChange={() => !isDisabled && setDeliveryMethod(point.pickup_code)}
                        disabled={isDisabled}
                      />
                      <DeliveryInfo>
                        <DeliveryTitle>🏪 Самовывоз: {point.name}</DeliveryTitle>
                        <DeliveryDescription>
                          {point.address && <>{point.address}<br/></>}
                          {point.working_hours && formatWorkingHours(point.working_hours)}
                        </DeliveryDescription>
                        {isDisabled && itemsMessages && itemsMessages.length > 0 && (
                          <UnavailableItemsList>
                            {itemsMessages.map((message, index) => (
                              <UnavailableItem key={index}>
                                ⚠️ {message}
                              </UnavailableItem>
                            ))}
                          </UnavailableItemsList>
                        )}
                      </DeliveryInfo>
                      <DeliveryPrice free>Бесплатно</DeliveryPrice>
                    </DeliveryOption>
                  );
                })}
                
                {/* СДЭК */}
                <DeliveryOption 
                  selected={deliveryMethod === 'cdek'}
                  onClick={() => {
                    setDeliveryMethod('cdek');
                    if (!selectedCdekPvz) {
                      setShowCdekModal(true);
                    }
                  }}
                >
                  <input 
                    type="radio" 
                    name="delivery" 
                    value="cdek"
                    checked={deliveryMethod === 'cdek'}
                    onChange={() => {
                      setDeliveryMethod('cdek');
                      if (!selectedCdekPvz) {
                        setShowCdekModal(true);
                      }
                    }}
                  />
                  <DeliveryInfo>
                    <DeliveryTitle>🚚 Доставка СДЭК</DeliveryTitle>
                    <DeliveryDescription>
                      Курьерская доставка или пункт выдачи<br/>
                      Срок: 3-5 рабочих дней
                    </DeliveryDescription>
                    
                    {/* Показываем выбранную доставку СДЭК */}
                    {selectedCdekPvz && deliveryMethod === 'cdek' && (
                      <SelectedPvzInfo>
                        <strong>Выбрана доставка СДЭК:</strong><br/>
                        {cdekDeliveryType === 'office' ? '📦 Пункт выдачи' : '🚚 Доставка курьером'}<br/>
                        <span style={{ color: '#666', fontSize: '14px' }}>
                          {selectedCdekPvz.address || selectedCdekPvz.formatted}<br/>
                          {cdekTariff && `Срок: ${cdekTariff.period_max} дн.`}
                          {cdekTariff && ` • Стоимость: ${isFreeDelivery ? 'Бесплатно' : `${Math.round(cdekTariff.delivery_sum)} ₽`}`}
                        </span>
                        <SelectPvzButton 
                          type="button" 
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowCdekModal(true);
                          }}
                        >
                          Изменить
                        </SelectPvzButton>
                      </SelectedPvzInfo>
                    )}
                    
                    {/* Кнопка выбора если ещё не выбрано */}
                    {!selectedCdekPvz && deliveryMethod === 'cdek' && (
                      <SelectPvzButton 
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowCdekModal(true);
                        }}
                      >
                        Выбрать способ доставки
                      </SelectPvzButton>
                    )}
                  </DeliveryInfo>
                  <DeliveryPrice free={isFreeDelivery || (cdekTariff && cdekTariff.delivery_sum === 0)}>
                    {isFreeDelivery ? 'Бесплатно' : 
                      cdekTariff ? (cdekTariff.delivery_sum === 0 ? 'Бесплатно' : `${Math.round(cdekTariff.delivery_sum)} ₽`) : 'Выберите ПВЗ'}
                  </DeliveryPrice>
                </DeliveryOption>
                
                {/* 5Post */}
                <DeliveryOption 
                  selected={deliveryMethod === '5post'}
                  onClick={() => {
                    setDeliveryMethod('5post');
                    if (!selectedPvz) {
                      openPvzModal();
                    }
                  }}
                >
                  <input 
                    type="radio" 
                    name="delivery" 
                    value="5post"
                    checked={deliveryMethod === '5post'}
                    onChange={() => {
                      setDeliveryMethod('5post');
                      if (!selectedPvz) {
                        openPvzModal();
                      }
                    }}
                  />
                  <DeliveryInfo>
                    <DeliveryTitle>📦 Доставка 5Post</DeliveryTitle>
                    <DeliveryDescription>
                      Постаматы и пункты выдачи<br/>
                      Срок: 3-7 рабочих дней
                    </DeliveryDescription>
                    
                    {/* Показываем выбранный ПВЗ */}
                    {selectedPvz && deliveryMethod === '5post' && (
                      <SelectedPvzInfo>
                        <strong>Выбранный пункт выдачи:</strong><br/>
                        {selectedPvz.name}
                        {selectedPvz.pvz_type === 'postamat' || selectedPvz.type === 'postamat' ? 
                          <PvzType type="postamat">Постамат</PvzType> : 
                          <PvzType type="pvz">ПВЗ</PvzType>
                        }<br/>
                        <span style={{ color: '#666', fontSize: '14px' }}>
                          {selectedPvz.address}<br/>
                          {selectedPvz.working_hours || selectedPvz.workingHours}
                        </span>
                        <SelectPvzButton 
                          type="button" 
                          onClick={(e) => {
                            e.stopPropagation();
                            openPvzModal();
                          }}
                        >
                          Изменить пункт выдачи
                        </SelectPvzButton>
                      </SelectedPvzInfo>
                    )}
                    
                    {/* Кнопка выбора ПВЗ если ещё не выбран */}
                    {!selectedPvz && deliveryMethod === '5post' && (
                      <SelectPvzButton 
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          openPvzModal();
                        }}
                      >
                        Выбрать пункт выдачи
                      </SelectPvzButton>
                    )}
                  </DeliveryInfo>
                  <DeliveryPrice free={isFreeDelivery || (deliveryMethod === '5post' && deliveryCost === 0 && selectedPvz)}>
                    {isFreeDelivery ? 'Бесплатно' : 
                      (deliveryMethod === '5post' && deliveryCost > 0 ? `${deliveryCost} ₽` : 
                       deliveryMethod === '5post' && selectedPvz && deliveryCost === 0 ? 'Бесплатно' : 'Выберите ПВЗ')}
                  </DeliveryPrice>
                </DeliveryOption>
              </DeliveryOptions>
              
              {isFreeDelivery && (!deliveryMethod || !deliveryMethod.startsWith('pickup_')) && (
                <FreeDeliveryNote>
                  ✅ При заказе от 10 000 ₽ (после скидки) доставка бесплатная!
                </FreeDeliveryNote>
              )}
            </FormSection>

            {/* Адрес доставки показываем только если выбрана доставка, а не самовывоз */}
            {needsAddress && (
            <FormSection>
              <SectionTitle>Адрес доставки</SectionTitle>
              
              <FormGroup>
                <Label htmlFor="address">Адрес *</Label>
                <Input
                  type="text"
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  required={needsAddress}
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="city">Город *</Label>
                <Input
                  type="text"
                  id="city"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  required={needsAddress}
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="postalCode">Почтовый индекс</Label>
                <Input
                  type="text"
                  id="postalCode"
                  name="postalCode"
                  value={formData.postalCode}
                  onChange={handleInputChange}
                />
              </FormGroup>
            </FormSection>
            )}

            <FormSection>
              <SectionTitle>Дополнительная информация</SectionTitle>
              
              <FormGroup>
                <Label htmlFor="comment">Комментарий к заказу</Label>
                <TextArea
                  id="comment"
                  name="comment"
                  value={formData.comment}
                  onChange={handleInputChange}
                  placeholder="Укажите дополнительные пожелания по доставке..."
                />
              </FormGroup>
            </FormSection>

            <BackButton type="button" onClick={handleBackToCart}>
              Вернуться в корзину
            </BackButton>

            <SubmitButton type="submit" disabled={loading || (deliveryMethod === '5post' && !selectedPvz) || (deliveryMethod === 'cdek' && !selectedCdekPvz)}>
              {loading ? 'Переходим к оплате...' : 'Перейти к оплате'}
            </SubmitButton>
            
            {deliveryMethod === '5post' && !selectedPvz && (
              <div style={{ marginTop: '10px', color: '#ff6b35', textAlign: 'center', fontSize: '14px' }}>
                ⚠️ Пожалуйста, выберите пункт выдачи 5Post
              </div>
            )}
            
            {deliveryMethod === 'cdek' && !selectedCdekPvz && (
              <div style={{ marginTop: '10px', color: '#ff6b35', textAlign: 'center', fontSize: '14px' }}>
                ⚠️ Пожалуйста, выберите способ доставки СДЭК
              </div>
            )}
          </form>
        </CheckoutForm>
      </CheckoutContent>
      
      {/* Модальное окно выбора ПВЗ 5Post */}
      {showPvzModal && (
        <ModalOverlay onClick={() => setShowPvzModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>📦 Выберите пункт выдачи 5Post</ModalTitle>
              <CloseButton onClick={() => setShowPvzModal(false)}>×</CloseButton>
            </ModalHeader>
            <ModalBody>
              <SearchInput
                type="text"
                placeholder="Поиск по адресу или названию..."
                value={pvzSearch}
                onChange={handlePvzSearch}
              />
              
              <MapToggleRow>
                <MapToggleButton
                  active={!useWidgetMap}
                  onClick={() => setUseWidgetMap(false)}
                >
                  📋 Список
                </MapToggleButton>
                <MapToggleButton
                  active={useWidgetMap}
                  onClick={() => setUseWidgetMap(true)}
                >
                  🗺️ Карта
                </MapToggleButton>
              </MapToggleRow>
              
              {useWidgetMap ? (
                <>
                  {widgetError ? (
                    <div style={{ textAlign: 'center', padding: '20px', color: '#f44336', background: '#ffebee', borderRadius: '8px', marginBottom: '16px' }}>
                      <div style={{ fontWeight: '600', marginBottom: '8px' }}>⚠️ Ошибка карты</div>
                      <div style={{ marginBottom: '12px' }}>{widgetError}</div>
                      <button 
                        onClick={() => {
                          setWidgetError(null);
                          initFivePostCustomMap();
                        }}
                        style={{ padding: '8px 16px', background: '#ff6b35', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
                      >
                        Попробовать снова
                      </button>
                    </div>
                  ) : (
                    <MapContainer id="fivepost-map-container">
                      {pvzLoading && (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#666' }}>
                          <div>🔄 Загрузка карты...</div>
                        </div>
                      )}
                      {!pvzLoading && (!pvzList || pvzList.length === 0) && (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#666', textAlign: 'center', padding: '20px' }}>
                          <div>
                            <div style={{ marginBottom: '8px' }}>Нет пунктов выдачи для выбранного города.</div>
                            <div style={{ fontSize: '14px' }}>Введите город выше или воспользуйтесь списком.</div>
                          </div>
                        </div>
                      )}
                      {!pvzLoading && pvzList?.length > 0 && (
                        <div id="fivepost-map" style={{ width: '100%', height: '100%', minHeight: '400px' }} />
                      )}
                    </MapContainer>
                  )}
                </>
              ) : (
                <>
              {pvzLoading ? (
                <LoadingSpinner>
                  <div>🔄 Загрузка пунктов выдачи...</div>
                </LoadingSpinner>
              ) : pvzError ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#f44336', background: '#ffebee', borderRadius: '8px' }}>
                  <div style={{ fontWeight: '600', marginBottom: '8px' }}>⚠️ Ошибка загрузки</div>
                  <div>{pvzError}</div>
                  <button 
                    onClick={() => loadFivePostPvz(pvzSearch || formData.city)}
                    style={{ marginTop: '16px', padding: '8px 16px', background: '#ff6b35', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
                  >
                    Попробовать снова
                  </button>
                </div>
              ) : filteredPvzList.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                  {pvzSearch ? 
                    'Пункты выдачи не найдены. Попробуйте изменить поисковый запрос.' :
                    'Введите город для поиска пунктов выдачи.'
                  }
                </div>
              ) : (
                <PvzList>
                  {filteredPvzList.map((pvz) => (
                    <PvzItem
                      key={pvz.code || pvz.pvz_code}
                      selected={selectedPvz?.code === pvz.code || selectedPvz?.pvz_code === pvz.pvz_code}
                      onClick={() => handlePvzSelect(pvz)}
                    >
                      <PvzName>
                        {pvz.name}
                        {pvz.pvz_type === 'postamat' || pvz.type === 'postamat' ? 
                          <PvzType type="postamat">Постамат</PvzType> : 
                          <PvzType type="pvz">ПВЗ</PvzType>
                        }
                      </PvzName>
                      <PvzAddress>{pvz.address || pvz.full_address}</PvzAddress>
                      {(pvz.working_hours || pvz.workingHours) && (
                        <PvzWorkingHours>
                          🕐 {pvz.working_hours || pvz.workingHours}
                        </PvzWorkingHours>
                      )}
                      {pvz.metro && (
                        <div style={{ fontSize: '13px', color: '#1976d2', marginTop: '4px' }}>
                          🚇 {pvz.metro}
                        </div>
                      )}
                    </PvzItem>
                  ))}
                </PvzList>
              )}
                </>
              )}
            </ModalBody>
          </ModalContent>
        </ModalOverlay>
      )}
      
      {/* Модальное окно выбора доставки СДЭК */}
      {showCdekModal && (
        <ModalOverlay onClick={() => setShowCdekModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>🚚 Выберите способ доставки СДЭК</ModalTitle>
              <CloseButton onClick={() => setShowCdekModal(false)}>×</CloseButton>
            </ModalHeader>
            <ModalBody>
              {/* Виджет сам отображает карту и список ПВЗ */}
              <MapContainer id="cdek-map" style={{ height: '600px', width: '100%' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#666' }}>
                  <div>🔄 Загрузка карты СДЭК...</div>
                </div>
              </MapContainer>
            </ModalBody>
          </ModalContent>
        </ModalOverlay>
      )}
    </CheckoutContainer>
  );
};

export default CheckoutPage;
